
import React from 'react';
import { useSelector } from 'react-redux';
import { mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';
import { Stack } from 'office-ui-fabric-react/lib/Stack';

import Text from '../../common/client/components/Text';

import {
    getAnalyzeData, getAnalyzeResultBySelectedTel, getOpsubForOpsub, getParForOpsub,
    getSelectedTel
} from '../../reducers/analyze';
import { getIsknDateTitle } from '../../reducers/iskn';
import { getOpsubName } from '../../reducers/opsub';

import AnalyzeResultPanel from './AnalyzeResultPanel';
import OverviewPanel from './OverviewPanel';
import SelectLvDialog from './SelectLvDialog';

const classNames = mergeStyleSets({
    OverviewPanelAnalyze: {
        paddingTop: 16
    },
    AnalyzeResultSet: {
    },
    resultSetContainer1: {
        width: 1, // hack for scroll
        //overflowX: 'scroll'
    },
    resultSetContainer2: {
        height: 1, // hack for scroll
        overflowY: 'auto'
    },
    resultContainer: {
        backgroundColor: '#eeeeee',
        margin: 4
    },
    resultTitle: {
        backgroundColor: '#bbbbbb',
        padding: 4,
        '&:hover': {
            backgroundColor: '#0078d4',
            color: '#ffffff',
            cursor: 'pointer'
        }
    },
    resultContent: {
        backgroundColor: '#eeeeee',
        width: 250,
        padding: 4
    },
    opsubName: {
        width: 150,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
    },
    inputStatic: {
        padding: 4
    },
    selectedLv: {
        padding: 4,
        backgroundColor: '#eeeeee',
        '&:hover': {
            backgroundColor: '#0078d4',
            color: '#ffffff',
            cursor: 'pointer'
        }
    },
    selectedLv2: {
        backgroundColor: '#ffb900',
    }
});

function calcParcelaVymera(par) {
    let vymera = par.reduce((previous, current) => {
        return (previous + parseInt(current.vymera_parcely));
    }, 0);
    vymera = (vymera / 10000).toFixed(1) + ' ha';
    return vymera;
}

const OpsubItem = ({opsubItem, analyzeResult}) => {
    const par = getParForOpsub(analyzeResult, opsubItem.opsub_id);
    const opsubName = getOpsubName(opsubItem);
    const vymera = calcParcelaVymera(par);
    return (
        <Stack horizontal verticalAlign="end" tokens={{childrenGap: 8}}>
            <Stack.Item grow>
                <div className={classNames.opsubName} title={opsubName}>
                    <Text bold>{opsubName}</Text>
                </div>
            </Stack.Item>
            <Text>({par.length})</Text>
            <Text nowrap>{vymera}</Text>
        </Stack>
    );
}

const OpsubItems = ({analyzeResult}) => {
    return (
        <Stack>
            {analyzeResult.opsub.map(item => {
                return (
                    <OpsubItem
                        key={item.opsub_id}
                        opsubItem={item}
                        analyzeResult={analyzeResult}
                    />
                );
            })}
        </Stack>
    );
}

const AnalyzeResult = ({analyzeResult}) => {

    const [open, setOpen] = React.useState(false);
    const iskn = useSelector(state => state.iskn);

    const isknDateTitle = getIsknDateTitle(iskn, analyzeResult.isknDate);
    const handleClick = () => {
        setOpen(true);
    }

    const handleDismiss = () => {
        setOpen(false);
    }

    return (
        <Stack className={classNames.resultContainer}>
            <Stack horizontal className={classNames.resultTitle} tokens={{childrenGap: 8}} onClick={handleClick}>
                <Stack.Item grow>
                    <Text>{isknDateTitle}</Text>
                </Stack.Item>
                <Text>({analyzeResult.par.length})</Text>
                <Text>{calcParcelaVymera(analyzeResult.par)}</Text>
            </Stack>
            <Stack.Item className={classNames.resultContent}>
                <OpsubItems
                    analyzeResult={analyzeResult}
                />
            </Stack.Item>
            <AnalyzeResultPanel
                open={open}
                analyzeResult={analyzeResult}
                isknDateTitle={isknDateTitle}
                onDismiss={handleDismiss}
            />
        </Stack>
    );
}

const AnalyzeResultSet = ({analyzeData}) => {

    const selectedTel = useSelector(state => getSelectedTel(state.analyze));

    return (
        <Stack verticalFill horizontal className={classNames.AnalyzeResultSet}>
            <Stack.Item grow className={classNames.resultSetContainer1}>
                <Stack verticalFill>
                    <Stack.Item grow className={classNames.resultSetContainer2}>
                        <Stack verticalFill horizontal>
                            {analyzeData.output.data.map(item => {
                                return (
                                    <AnalyzeResult
                                        key={item.isknDate}
                                        analyzeResult={getAnalyzeResultBySelectedTel(item, selectedTel)}
                                    />
                                );
                            })}

                        </Stack>
                </Stack.Item>
                </Stack>
            </Stack.Item>
        </Stack>
    );
}

const AnalyzeInput = ({analyzeData}) => {

    const [open, setOpen] = React.useState(false);

    const iskn = useSelector(state => state.iskn);
    const selectedTel = useSelector(state => getSelectedTel(state.analyze));

    let selectedTelCount = selectedTel.reduce((previous, current) => {
        return (current.selected)
            ? previous + 1
            : previous;
    }, 0);

    const analyzeResult = analyzeData.output.data[0];
    const opsubItems = getOpsubForOpsub(analyzeResult, analyzeData.input.opsubId);
    const opsub = opsubItems[0];

    const isknDateTitle = getIsknDateTitle(iskn, analyzeResult.isknDate);

    const getSelectedTelTitle = () => {
        return (selectedTelCount === selectedTel.length)
            ? `Vše`
            : `Vybrané ${selectedTelCount} z ${selectedTel.length}`;
    }

    const getSelectedTelClassName = () => {
        return (selectedTelCount === selectedTel.length)
            ? classNames.selectedLv
            : [classNames.selectedLv, classNames.selectedLv2].join(' ')
    }

    const handleClickSelectLv = () => {
        setOpen(true);
    }

    const handleDismissSelectLv = () => {
        setOpen(false);
    }

    return (
        <Stack horizontal tokens={{childrenGap: 16}}>
            <Stack.Item className={classNames.inputStatic}>
                <Text>Pro: </Text>
                <Text bold>{opsub._name}, </Text>
                <Text>{opsub._rcico}, </Text>
                <Text>{opsub._address}</Text>
            </Stack.Item>
            <Stack.Item className={classNames.inputStatic}>
                <Text>Od datové sady: </Text>
                <Text bold>{isknDateTitle}</Text>
            </Stack.Item>
            <Stack.Item className={classNames.inputStatic}>
                <Text>Směr analýzy: </Text>
                <Text bold>{(analyzeData.input.direction === 'forward') ? 'Do budoucnosti' : 'Do minulosti'}</Text>
            </Stack.Item>
            <Stack.Item className={getSelectedTelClassName()} onClick={handleClickSelectLv}>
                <Text>Vybrané LV: </Text>
                <Text bold>{getSelectedTelTitle()}</Text>
            </Stack.Item>
            <SelectLvDialog
                open={open}
                onDismiss={handleDismissSelectLv}
                analyzeData={analyzeData}
            />
        </Stack>
    );
}

const OverviewPanelAnalyze = (props) => {

    const analyze = useSelector(state => state.analyze);
    const analyzeData = useSelector(state => getAnalyzeData(state.analyze));

    return (
        <OverviewPanel title="ANALÝZA VLASTNICTVÍ" loaded={analyze.loaded} panelWidth="auto" panelHeight="100%">
            <Stack verticalFill className={classNames.OverviewPanelAnalyze} tokens={{childrenGap: 16}}>
                <AnalyzeInput
                    analyzeData={analyzeData}
                />
                <AnalyzeResultSet
                    analyzeData={analyzeData}
                />
            </Stack>
        </OverviewPanel>
    );
}

export default OverviewPanelAnalyze;
